import type { ReactElement } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import type { TopicCandidate } from '@contracts/topics';
import { useMergeTopic, useTopicCandidates } from '../api/queries';
import { Broken, Button, Modal, Pill, SearchField, SkeletonRows, useNotices } from '../ui';
import { matches } from '../lib/match';
import { ago } from '../lib/time';

/**
 * "Same work as…" — folding this thread into another of your own (2026-09-24).
 *
 * The linker decides which thread a new meeting continues, and it misses:
 * "Pilot invoicing" and "Pilot update design task" were one piece of work in
 * two threads, each with half the meetings and half a "where it stands". A
 * person says so here, and the two become one.
 *
 * ⚠ The closest by meaning come first, marked — that is where a missed thread
 * almost always is, and a person should not have to know its other name to
 * find it. Search reaches every other thread they have.
 */
export function SameWork({
  topic,
  onClose,
}: {
  topic: { id: string; name: string };
  onClose: () => void;
}): ReactElement {
  const candidates = useTopicCandidates(topic.id);
  const merge = useMergeTopic();
  const navigate = useNavigate();
  const { show } = useNotices();
  const [needle, setNeedle] = useState('');
  const [into, setInto] = useState<TopicCandidate | null>(null);
  const [keepName, setKeepName] = useState(false);

  const go = () => {
    if (!into) return;
    merge.mutate(
      { id: topic.id, body: { into: into.id, keepName } },
      {
        onSuccess: (left) => {
          onClose();
          void navigate(`/t/${left.id}`, { replace: true });
          show({
            tone: 'success',
            text: `One thread now — “${keepName ? topic.name : into.name}”. Where it stands is being written again from all of its meetings.`,
          });
        },
        onError: (err) => show({ tone: 'failure', text: err.message }),
      },
    );
  };

  const all = candidates.data?.topics ?? [];
  const shown = all.filter((c) => matches(needle, c.name, c.description, ...c.placeNames));

  return (
    <Modal
      title="Same work as…"
      onClose={onClose}
      foot={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!into}
            onClick={go}
            working={merge.isPending ? 'Joining…' : false}
          >
            Make them one
          </Button>
        </>
      }
    >
      <p className="t-secondary">
        Pick the thread “{topic.name}” is the same work as. Its meetings join that one, where it
        stands is written again from all of them, and this page goes away.
      </p>
      <SearchField
        kbd=""
        autoFocus
        placeholder={all.length ? `Find one of your ${all.length} other topics…` : 'Find a topic…'}
        value={needle}
        onChange={(e) => setNeedle(e.target.value)}
        aria-label="Find a topic"
      />
      <div className="same-work-list">
        {candidates.isPending ? <SkeletonRows rows={4} /> : null}
        {candidates.isError ? (
          <Broken what="your other topics" retry={() => void candidates.refetch()} />
        ) : null}
        {candidates.isSuccess && shown.length === 0 ? (
          <p className="t-secondary same-work-none">
            {all.length === 0
              ? 'You have no other topics yet.'
              : `Nothing of yours matches “${needle}”.`}
          </p>
        ) : null}
        {shown.map((c) => (
          <button
            key={c.id}
            type="button"
            className={`row dense same-work-row ${into?.id === c.id ? 'on' : ''}`}
            aria-pressed={into?.id === c.id}
            onClick={() => setInto(c)}
          >
            <span className="row-main">
              <span className="row-title truncate">{c.name}</span>
              <span className="row-sub">
                {c.mentions} {c.mentions === 1 ? 'meeting' : 'meetings'}
                {c.placeNames.length ? ` · ${c.placeNames.slice(0, 2).join(' · ')}` : ''} · last{' '}
                {ago(c.lastMentionedAt)}
              </span>
            </span>
            {c.likely ? <Pill tone="green">close</Pill> : null}
          </button>
        ))}
      </div>
      {into ? (
        <div className="stack" style={{ gap: 6 }}>
          <span className="t-secondary">Call it</span>
          {/* ⚠ Both names offered, the picked thread's first: it is the one
              that stays, and its name is what its meetings have called it. */}
          <div className="cluster" style={{ gap: 6 }}>
            <Button
              size="sm"
              variant={keepName ? 'quiet' : 'primary'}
              onClick={() => setKeepName(false)}
            >
              {into.name}
            </Button>
            <Button
              size="sm"
              variant={keepName ? 'primary' : 'quiet'}
              onClick={() => setKeepName(true)}
            >
              {topic.name}
            </Button>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
